import type { Lang } from "../index";

export interface TechLeadOneOnOneCopy {
  seo: { title: string; description: string };
  eyebrow: string;
  h1: string;
  lede: string;
  problem: { heading: string; body1: string; body2: string };
  scenarios: {
    heading: string;
    items: { tag: string; title: string; body: string }[];
  };
  how: {
    heading: string;
    body: string;
    bullets: string[];
  };
  proof: { heading: string; bullets: string[] };
  cta: { heading: string; body: string; primary: string; secondary: string };
}

const en: TechLeadOneOnOneCopy = {
  seo: {
    title: "Minuta for tech leads — live copilot for English 1-on-1s",
    description:
      "Tech lead in a difficult English 1-on-1. Minuta drafts calibrated feedback, architecture alignment, and stakeholder replies during the call — no bot, zero-retention cloud AI.",
  },
  eyebrow: "Use case · Tech lead",
  h1: "The hard question lands in English. Answer like the lead you are.",
  lede:
    "Tech leads don't struggle with the technical judgment. They struggle when feedback, architecture tradeoffs, deadline risk, and stakeholder alignment have to be phrased in English under live pressure. Minuta is the copilot that drafts the next answer in your tone while the conversation is still moving.",
  problem: {
    heading: "The meeting is not hard because of the notes",
    body1:
      "It's the 1-on-1 where a senior engineer asks if their promotion is on track, then the call turns into the design review that missed the mark. Or the stakeholder sync where a PM asks whether the migration deadline is still credible. You know the technical truth. You know the human cost of saying it badly. The hard part is producing the calibrated English sentence while everyone is waiting.",
    body2:
      "Generic notetakers help after the call. That's too late for the moment that decides whether the engineer understands the feedback, whether the stakeholder trusts the risk assessment, or whether the team leaves aligned. Minuta sits on your side of the call: prior notes and docs loaded, live transcript visible, 2-4 second answer draft ready when the sensitive question appears.",
  },
  scenarios: {
    heading: "Where it pays off",
    items: [
      {
        tag: "Sensitive feedback",
        title: "\"You over-engineered the cache layer\"",
        body: "You loaded their design doc, the refactor PR, and your last 1-on-1 notes. When the feedback moment lands, Minuta drafts: \"The concern is not effort; it's impact. The extra cache layer added complexity the team paid for during the refactor, so next time I want you to validate the simpler path first.\" You edit one phrase and say it. The engineer leaves with the point, not a bruise.",
      },
      {
        tag: "Performance concern",
        title: "\"Am I still on track for senior?\"",
        body: "You attached the growth plan, PR review history, and the two examples where ownership slipped. The engineer asks directly. Minuta suggests a reply that separates potential from current evidence: \"Yes, the path is still open, but the signal I need to see next is consistent follow-through after design decisions.\" The call moves from defensiveness to a concrete 30-day bar.",
      },
      {
        tag: "Architecture alignment",
        title: "\"Why aren't we moving to microservices now?\"",
        body: "The RFC, incident notes, and migration constraints are loaded before the call. As the debate heats up, Minuta drafts a calm technical frame: acknowledge the scalability argument, name the current operational bottleneck, and propose the decision rule for revisiting it. You keep the conversation on tradeoffs instead of taste.",
      },
      {
        tag: "Deadline risk",
        title: "\"Can we still ship this by Friday?\"",
        body: "You loaded sprint scope, dependency notes, and the blocker thread. When the PM asks for a yes/no, Minuta drafts the honest answer in stakeholder-safe English: \"We can ship the core path by Friday if we cut the reporting edge case; keeping both puts quality at risk.\" The outcome changes from vague optimism to an explicit scope decision.",
      },
      {
        tag: "Stakeholder alignment",
        title: "\"Why did engineering push back on this?\"",
        body: "You attached the product brief, Slack thread, and last decision log. Minuta suggests wording that protects the team without sounding political: \"The pushback is about sequencing, not ownership. We agree on the customer problem; we need to land the data model change first so the launch does not create rework.\" The stakeholder hears a plan, not an excuse.",
      },
    ],
  },
  how: {
    heading: "How Minuta works during the call",
    body:
      "Open Minuta a minute before the Zoom/Meet/Teams call. Attach the prior 1-on-1 notes, growth doc, RFC, decision log, sprint scope, stakeholder brief — whatever you would normally skim before a difficult conversation. Then run the call as you always would.",
    bullets: [
      "No bot. The IC sees nothing in the call — Minuta captures system audio of the Zoom/Meet/Teams 1-on-1 from your desktop.",
      "Live English transcript on the side. Useful when an IC, PM, or stakeholder is fast, accented, or emotionally heated and you need the exact question.",
      "Sensitive question appears → you hit the copilot → 2-4 second draft in your tone, grounded in the docs you loaded before the call.",
      "After the call: structured summary with what they said, what you decided, what you committed, and action items. Drop it into your private notes — no separate write-up needed.",
    ],
  },
  proof: {
    heading: "Why tech leads pick Minuta over generic notetakers",
    bullets: [
      "Otter, Tactiq, Fireflies give you a transcript and a generic summary. None of them help you answer the sensitive question while it is being asked.",
      "A bot in a 1-on-1 or tense stakeholder call breaks trust — people reasonably ask why a third party is present. Minuta is invisible.",
      "Your prior 1-on-1 notes, growth docs, RFCs, decision logs, and sprint context ground every drafted reply. No generic management-speak.",
      "Zero-retention security. The audio of performance feedback, architecture disagreement, and deadline risk is processed through reviewed cloud AI paths without permanent storage.",
    ],
  },
  cta: {
    heading: "Try it before your next 1-on-1",
    body: "7 days with everything unlocked. No credit card. €9/mo after.",
    primary: "Start free trial",
    secondary: "Download for macOS",
  },
};

const pt: TechLeadOneOnOneCopy = {
  seo: {
    title: "Minuta pra tech lead — copiloto ao vivo em 1-on-1 em inglês",
    description:
      "Tech lead numa conversa difícil em inglês. Minuta sugere feedback calibrado, alinhamento de arquitetura e resposta pra stakeholder durante a call — sem bot, seguro.",
  },
  eyebrow: "Caso de uso · Tech lead",
  h1: "A pergunta difícil cai em inglês. Responda como o lead que você é.",
  lede:
    "Tech lead não tropeça no julgamento técnico. Tropeça quando feedback, tradeoff de arquitetura, risco de deadline e alinhamento com stakeholder precisam sair em inglês sob pressão ao vivo. O Minuta é o copiloto que sugere a próxima resposta no seu tom enquanto a conversa ainda está andando.",
  problem: {
    heading: "A reunião não é difícil por causa da ata",
    body1:
      "É o 1-on-1 em que o engenheiro sênior pergunta se ainda está no caminho da promoção, e a conversa vira o design review que não funcionou. Ou o sync com stakeholder em que a PM pergunta se o deadline da migração ainda é crível. Você sabe a verdade técnica. Sabe o custo humano de falar mal. O difícil é produzir a frase calibrada em inglês enquanto todo mundo espera.",
    body2:
      "Notetaker genérico ajuda depois da call. Tarde demais pro momento que decide se o engenheiro entendeu o feedback, se o stakeholder confiou no risco, ou se o time saiu alinhado. O Minuta fica do seu lado da call: notas e docs carregados, transcript ao vivo visível, resposta sugerida em 2-4 segundos quando a pergunta sensível aparece.",
  },
  scenarios: {
    heading: "Onde paga o investimento",
    items: [
      {
        tag: "Feedback sensível",
        title: "\"Você over-engineered a cache layer\"",
        body: "Você carregou o design doc, o PR de refactor e as notas do último 1-on-1. Quando chega o momento do feedback, o Minuta sugere: \"The concern is not effort; it's impact. The extra cache layer added complexity the team paid for during the refactor, so next time I want you to validate the simpler path first.\" Você troca uma frase e fala. O engenheiro sai com o ponto, não com uma ferida.",
      },
      {
        tag: "Performance concern",
        title: "\"Am I still on track for senior?\"",
        body: "Você anexou o growth plan, histórico de PR review e os dois exemplos em que ownership escorregou. O engenheiro pergunta direto. O Minuta sugere uma resposta que separa potencial de evidência atual: \"Yes, the path is still open, but the signal I need to see next is consistent follow-through after design decisions.\" A call sai da defensiva e vira uma barra concreta de 30 dias.",
      },
      {
        tag: "Alinhamento técnico",
        title: "\"Why aren't we moving to microservices now?\"",
        body: "O RFC, as notas de incidente e as restrições da migração já estão carregados. Quando o debate esquenta, o Minuta monta um frame técnico calmo: reconhecer o argumento de escala, nomear o gargalo operacional atual e propor a regra de decisão pra revisitar. Você mantém a conversa em tradeoffs, não em gosto pessoal.",
      },
      {
        tag: "Risco de deadline",
        title: "\"Can we still ship this by Friday?\"",
        body: "Você carregou o scope da sprint, notas de dependência e a thread do blocker. Quando a PM pede sim/não, o Minuta sugere a resposta honesta em inglês seguro pra stakeholder: \"We can ship the core path by Friday if we cut the reporting edge case; keeping both puts quality at risk.\" O resultado muda de otimismo vago pra decisão explícita de escopo.",
      },
      {
        tag: "Stakeholder alignment",
        title: "\"Why did engineering push back on this?\"",
        body: "Você anexou o brief de produto, a thread do Slack e o último decision log. O Minuta sugere um wording que protege o time sem soar político: \"The pushback is about sequencing, not ownership. We agree on the customer problem; we need to land the data model change first so the launch does not create rework.\" O stakeholder ouve plano, não desculpa.",
      },
    ],
  },
  how: {
    heading: "Como o Minuta ajuda durante a call",
    body:
      "Abre o Minuta um minuto antes da call no Zoom/Meet/Teams. Anexa notas dos 1-on-1 anteriores, growth doc, RFC, decision log, scope da sprint, brief do stakeholder — o que você normalmente releria antes de uma conversa difícil. Depois é só rodar a call como sempre roda.",
    bullets: [
      "Sem bot. O IC não vê nada na call — o Minuta captura o áudio do sistema do 1-on-1 no Zoom/Meet/Teams a partir do seu desktop.",
      "Transcript em inglês ao vivo do lado. Útil quando IC, PM ou stakeholder fala rápido, tem sotaque ou está emocionalmente alterado e você precisa da pergunta exata.",
      "Pergunta sensível aparece → você aciona o copiloto → sugestão em 2-4 segundos no seu tom, fundamentada nos docs que carregou antes da call.",
      "Depois da call: ata estruturada com o que disseram, o que foi decidido, com o que você se comprometeu e action items. Cola nas suas notas privadas — sem precisar escrever de novo.",
    ],
  },
  proof: {
    heading: "Por que tech lead escolhe Minuta em vez de notetaker genérico",
    bullets: [
      "Otter, Tactiq, Fireflies te dão transcript e resumo genérico. Nenhum te ajuda a responder a pergunta sensível enquanto ela está sendo feita.",
      "Bot num 1-on-1 ou numa call tensa com stakeholder quebra confiança — as pessoas perguntam, com razão, por que tem um terceiro ali. O Minuta é invisível.",
      "Suas notas de 1-on-1, growth docs, RFCs, decision logs e contexto da sprint fundamentam cada resposta sugerida. Sem management-speak genérico.",
      "Segurança com retenção zero. Áudio de feedback de performance, discordância de arquitetura e risco de prazo é processado por caminhos cloud revisados sem armazenamento permanente.",
    ],
  },
  cta: {
    heading: "Testa antes do seu próximo 1-on-1",
    body: "7 dias com tudo desbloqueado. Sem cartão. €9/mês depois.",
    primary: "Começar trial grátis",
    secondary: "Baixar pro macOS",
  },
};

const es: TechLeadOneOnOneCopy = {
  seo: {
    title: "Minuta para tech leads — copiloto en vivo para 1-on-1 en inglés",
    description:
      "Tech lead en una conversación difícil en inglés. Minuta sugiere feedback calibrado, alineación de arquitectura y respuestas a stakeholders durante la call — sin bot, seguro.",
  },
  eyebrow: "Caso de uso · Tech lead",
  h1: "Cae la pregunta difícil en inglés. Responde como el lead que eres.",
  lede:
    "Un tech lead no se traba en el juicio técnico. Se traba cuando feedback, tradeoffs de arquitectura, riesgo de deadline y alineación con stakeholders tienen que salir en inglés bajo presión en vivo. Minuta es el copiloto que sugiere la próxima respuesta en tu tono mientras la conversación sigue avanzando.",
  problem: {
    heading: "La reunión no es difícil por las notas",
    body1:
      "Es el 1-on-1 donde un ingeniero senior pregunta si sigue en camino a la promoción, y la conversación se vuelve el design review que falló. O el sync con stakeholders donde la PM pregunta si el deadline de la migración sigue siendo creíble. Sabes la verdad técnica. Sabes el costo humano de decirla mal. Lo difícil es producir la frase calibrada en inglés mientras todos esperan.",
    body2:
      "Un notetaker genérico ayuda después de la call. Demasiado tarde para el momento que decide si el ingeniero entendió el feedback, si el stakeholder confió en el riesgo, o si el equipo salió alineado. Minuta está de tu lado de la call: notas y docs cargados, transcript en vivo visible, respuesta sugerida en 2-4 segundos cuando aparece la pregunta sensible.",
  },
  scenarios: {
    heading: "Dónde paga la inversión",
    items: [
      {
        tag: "Feedback sensible",
        title: "\"Over-engineered la cache layer\"",
        body: "Cargaste el design doc, el PR de refactor y tus notas del último 1-on-1. Cuando llega el momento del feedback, Minuta sugiere: \"The concern is not effort; it's impact. The extra cache layer added complexity the team paid for during the refactor, so next time I want you to validate the simpler path first.\" Cambias una frase y lo dices. El ingeniero sale con el punto, no con una herida.",
      },
      {
        tag: "Performance concern",
        title: "\"Am I still on track for senior?\"",
        body: "Adjuntaste el growth plan, el historial de PR reviews y los dos ejemplos donde ownership se cayó. El ingeniero pregunta directo. Minuta sugiere una respuesta que separa potencial de evidencia actual: \"Yes, the path is still open, but the signal I need to see next is consistent follow-through after design decisions.\" La call sale de la defensiva y se convierte en una barra concreta de 30 días.",
      },
      {
        tag: "Alineación técnica",
        title: "\"Why aren't we moving to microservices now?\"",
        body: "El RFC, las notas de incidente y las restricciones de la migración ya están cargados. Cuando el debate se calienta, Minuta arma un marco técnico calmo: reconocer el argumento de escala, nombrar el cuello de botella operativo actual y proponer la regla de decisión para revisarlo. Mantienes la conversación en tradeoffs, no en gustos.",
      },
      {
        tag: "Riesgo de deadline",
        title: "\"Can we still ship this by Friday?\"",
        body: "Cargaste el scope del sprint, notas de dependencias y el thread del blocker. Cuando la PM pide sí/no, Minuta sugiere la respuesta honesta en inglés seguro para stakeholders: \"We can ship the core path by Friday if we cut the reporting edge case; keeping both puts quality at risk.\" El resultado cambia de optimismo vago a una decisión explícita de scope.",
      },
      {
        tag: "Stakeholder alignment",
        title: "\"Why did engineering push back on this?\"",
        body: "Adjuntaste el brief de producto, el thread de Slack y el último decision log. Minuta sugiere un wording que protege al equipo sin sonar político: \"The pushback is about sequencing, not ownership. We agree on the customer problem; we need to land the data model change first so the launch does not create rework.\" El stakeholder oye un plan, no una excusa.",
      },
    ],
  },
  how: {
    heading: "Cómo ayuda Minuta durante la call",
    body:
      "Abre Minuta un minuto antes de la call en Zoom/Meet/Teams. Adjunta notas de 1-on-1 anteriores, growth doc, RFC, decision log, scope del sprint, brief del stakeholder — lo que normalmente revisarías antes de una conversación difícil. Luego corres la call como siempre.",
    bullets: [
      "Sin bot. El IC no ve nada en la call — Minuta captura el audio del sistema del 1-on-1 en Zoom/Meet/Teams desde tu desktop.",
      "Transcript en inglés en vivo al lado. Útil cuando un IC, PM o stakeholder habla rápido, tiene acento o está alterado y necesitas la pregunta exacta.",
      "Aparece una pregunta sensible → activas el copiloto → sugerencia en 2-4 segundos en tu tono, basada en los docs que cargaste antes de la call.",
      "Después de la call: acta estructurada con lo que dijeron, lo que se decidió, a qué te comprometiste y action items. La pegas en tus notas privadas — sin escribir de nuevo.",
    ],
  },
  proof: {
    heading: "Por qué un tech lead elige Minuta en vez de un notetaker genérico",
    bullets: [
      "Otter, Tactiq, Fireflies te dan transcript y resumen genérico. Ninguno te ayuda a responder la pregunta sensible mientras te la están haciendo.",
      "Un bot en un 1-on-1 o en una call tensa con stakeholders rompe confianza — la gente pregunta, con razón, por qué hay un tercero ahí. Minuta es invisible.",
      "Tus notas de 1-on-1, growth docs, RFCs, decision logs y contexto del sprint fundamentan cada respuesta sugerida. Sin management-speak genérico.",
      "Seguridad con retención cero. El audio de feedback de rendimiento, desacuerdo de arquitectura y riesgo de entrega se procesa por rutas cloud revisadas sin almacenamiento permanente.",
    ],
  },
  cta: {
    heading: "Pruébalo antes de tu próximo 1-on-1",
    body: "7 días con todo desbloqueado. Sin tarjeta. €9/mes después.",
    primary: "Empezar prueba gratis",
    secondary: "Descargar para macOS",
  },
};

export const techLeadOneOnOneCopy: Record<Lang, TechLeadOneOnOneCopy> = { en, pt, es };
